import {
  UserModel,
  CommentModel,
  Model,
  ExerciseCommentAttachmentModel,
} from '@descomplica/dex-models';
import { CommentModel as ForumCommentModel } from '@/models/forumAPI/CommentModel';
import { formatScore } from '@/helpers/number';
import { ReactionInfo } from './ReactionsModel';
import { FileModel } from './FileModel';
import { CommentsByQuestionIdListType } from './undergrad/services/dex.services.model';
import { timeSinceCreation } from '@/helpers/date';

export class UnifiedCommentModel extends Model {
  id: number;

  text: string;

  userId: number;

  createdAt: string;

  score: number | null;

  attemptCount: number | null;

  textId: number | null;

  timeSinceCreation: string;

  commentType: 'forum' | 'dex';

  repliesId: number[];

  attachments: FileModel[];

  constructor(
    comment: CommentModel | ForumCommentModel | CommentsByQuestionIdListType,
    user?: UserModel,
  ) {
    if (comment instanceof CommentModel) {
      super(comment.getters);
      this.id = comment.id;
      this.text = comment.contents ? comment.contents[0].text.body : '';
      this.textId = comment.contents ? comment.contents[0].text.id : null;
      this.userId = comment.user.id;
      this.createdAt = comment.createdAt;
      this.score = comment.score;
      this.attemptCount = comment.attemptCount;
      this.timeSinceCreation = comment.timeSinceCreation;
      this.commentType = 'dex';
      this.repliesId = [];
      this.attachments = comment.exerciseCommentAttachments.map(
        (exerciseCommentAttachments: ExerciseCommentAttachmentModel) =>
          new FileModel({
            name: exerciseCommentAttachments.fileName,
            url: exerciseCommentAttachments.fileUrl,
            size: exerciseCommentAttachments.fileSize,
            type: exerciseCommentAttachments.fileType,
          }),
      );
    } else if (comment instanceof ForumCommentModel) {
      super(comment.getters);
      this.id = comment.id;
      this.text = comment.text;
      this.textId = null;
      this.userId = comment.userId;
      this.createdAt = comment.createdAt;
      this.score = comment.score;
      this.attemptCount = null;
      this.timeSinceCreation = comment.timeSinceCreation;
      this.commentType = 'forum';
      this.repliesId = comment.repliesId;
      this.attachments = comment.attachments;
    } else {
      super(user?.getters);
      this.id = comment.id;
      this.text = comment.content;
      this.createdAt = comment.updatedAt;
      this.textId = comment.textId;
      this.timeSinceCreation = timeSinceCreation(comment.updatedAt);
      this.attemptCount = comment.attemptCount;
      this.score = comment.score;
      this.commentType = 'dex';
      this.repliesId = [];
      this.attachments = comment.exerciseCommentAttachments?.map(
        (exerciseCommentAttachments) =>
          new FileModel({
            name: exerciseCommentAttachments.fileName,
            url: exerciseCommentAttachments.fileUrl,
            size: exerciseCommentAttachments.fileSize,
            type: exerciseCommentAttachments.fileType,
          }),
      );
      if (user) this.userId = user.id;
    }
  }

  get author(): UserModel {
    return this.getters.userById(this.userId);
  }

  get isUpdatingScore(): UserModel {
    return this.commentType === 'dex'
      ? this.getters.commentScoreUpdatingStatus(this.id)
      : this.getters.forumCommentScoreUpdatingStatus(this.id);
  }

  get formatedScore(): string {
    return formatScore(this.score, '-');
  }

  get contentReactionId(): string | null {
    return this.commentType && this.id ? `${this.commentType}-${this.id}` : null;
  }

  get reactions(): ReactionInfo[] {
    const contentReactions = this.getters.getContentReactions(this.contentReactionId);
    return contentReactions ? contentReactions.reactions || [] : [];
  }

  get replies(): UnifiedCommentModel[] {
    return this.repliesId.map((replyId) => this.getters.forumAPICommentById(replyId));
  }

  get userProfileAvatar(): string | null {
    const profile = this.getters.profilesByDexId(this.userId);
    return profile?.avatarUrl || null;
  }

  get userProfileName(): string | null {
    const profile = this.getters.profilesByDexId(this.userId);
    return profile?.displayName || null;
  }
}
